import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, CalendarIcon, UsersIcon, PlayIcon, PauseIcon, TrashIcon, PencilIcon, BarChart2, Mail, Send, Building2, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFeedbackList, useFeedbackVersions } from '../hooks/useFeedbackApi';
import {
  useFeedbackInstances,
  useCreateFeedbackInstance,
  useUpdateFeedbackInstance,
  useDeleteFeedbackInstance,
  useSaveEmailTemplate,
  useSendInvitations,
  useSendReminders,
} from '../hooks/useFeedbackInstanceApi';
import { useAuth } from '../context/AuthContext';
import { FeedbackInstance } from '../services/feedbackInstanceService';
import { AssignmentSelection } from '../services/deliverySearchService';
import EmailComposeModal from '../components/EmailComposeModal';
import EmailStatusBadge from '../components/EmailStatusBadge';
import EmailTemplateModal from '../components/EmailTemplateModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import AssignToModal from '../components/AssignToModal';

function toArray<T>(value: T | T[] | null | undefined | { items?: T[]; data?: T[] }): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object' && 'items' in value && Array.isArray(value.items)) return value.items;
  if (typeof value === 'object' && 'data' in value && Array.isArray(value.data)) return value.data;
  return [];
}

const InstanceManager: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const canManageInstances = effectiveRole === 'admin' || effectiveRole === 'creator';
  const { data: feedbackData, isLoading: loadingFeedback } = useFeedbackList();
  const { data: instancesData, isLoading: loadingInstances } = useFeedbackInstances();
  const createMutation = useCreateFeedbackInstance();
  const updateMutation = useUpdateFeedbackInstance();
  const deleteMutation = useDeleteFeedbackInstance();
  const saveTemplateMutation = useSaveEmailTemplate();
  const invitationsMutation = useSendInvitations();
  const remindersMutation = useSendReminders();

  const feedbackList = toArray(feedbackData).filter(s => s.isPublished);
  const instances = toArray(instancesData);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInstance, setEditingInstance] = useState<FeedbackInstance | null>(null);
  const [emailModal, setEmailModal] = useState<{
    instanceId: string;
    type: 'invitation' | 'reminder';
    feedbackTitle: string;
    deadline?: string;
    instance: FeedbackInstance;
  } | null>(null);
  const [pollingInstances, setPollingInstances] = useState<Set<string>>(new Set());
  const [selectedFeedbackId, setSelectedFeedbackId] = useState('');
  const [selectedVersionNo, setSelectedVersionNo] = useState<number>(1);
  const { data: versionsData } = useFeedbackVersions(selectedFeedbackId || undefined);
  const versions = toArray(versionsData);
  const publishedVersions = versions.filter(v => v.publishedAt);

  // New assignment state
  const [assignments, setAssignments] = useState<AssignmentSelection[]>([]);
  const [showAssignToModal, setShowAssignToModal] = useState(false);

  const [frequency, setFrequency] = useState<'once' | 'daily' | 'weekly' | 'monthly'>('once');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [sendEmailsNow, setSendEmailsNow] = useState(true);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [invitationSubject, setInvitationSubject] = useState('');
  const [invitationBody, setInvitationBody] = useState('');
  const [reminderSubject, setReminderSubject] = useState('');
  const [reminderBody, setReminderBody] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ feedback?: string; assignments?: string }>({});
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const isLegacyInstance = (instance: FeedbackInstance) => {
    return !instance.assignments || instance.assignments.length === 0;
  };

  const handleStartEdit = (instance: FeedbackInstance) => {
    setEditingInstance(instance);
    setSelectedFeedbackId(instance.feedbackId);
    setSelectedVersionNo(instance.versionNo);
    setFrequency(instance.frequency);

    if (instance.startDate.includes('T')) {
      const date = new Date(instance.startDate);
      setStartDate(date.toISOString().split('T')[0]);
      setStartTime(date.toISOString().split('T')[1]?.substring(0, 5) || '09:00');
    } else {
      setStartDate(instance.startDate);
      setStartTime('09:00');
    }

    setEndDate(instance.endDate ? (instance.endDate.includes('T') ? new Date(instance.endDate).toISOString().split('T')[0] : instance.endDate) : '');
    setSendEmailsNow(instance.autoSendInvitations);

    // Load assignments
    if (instance.assignments && instance.assignments.length > 0) {
      setAssignments(instance.assignments);
    } else {
      setAssignments([]);
    }

    // Load email templates
    setInvitationSubject(instance.customInvitationSubject || '');
    setInvitationBody(instance.customInvitationBody || '');
    setReminderSubject(instance.customReminderSubject || '');
    setReminderBody(instance.customReminderBody || '');

    setValidationErrors({});
    setShowCreateModal(true);
  };

  const handleSaveInstance = async () => {
    const errors: { feedback?: string; assignments?: string } = {};

    if (!selectedFeedbackId) {
      errors.feedback = 'Please select a feedback';
    }

    // For new instances or new-style edits, require assignments
    if (!editingInstance || !isLegacyInstance(editingInstance)) {
      if (assignments.length === 0) {
        errors.assignments = 'Please add at least one assignment';
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});

    const fullStartDate = frequency !== 'once'
      ? `${startDate}T${startTime}:00Z`
      : startDate;
    const fullEndDate = endDate
      ? (frequency !== 'once' ? `${endDate}T${startTime}:00Z` : endDate)
      : undefined;

    try {
      let savedInstance: FeedbackInstance;

      if (editingInstance) {
        const updateData: any = {
          frequency,
          startDate: fullStartDate,
          endDate: fullEndDate,
          autoSendInvitations: sendEmailsNow,
        };

        // Only send assignments for new-style instances
        if (!isLegacyInstance(editingInstance)) {
          updateData.assignments = assignments;
        }

        savedInstance = await updateMutation.mutateAsync({
          id: editingInstance.id,
          data: updateData,
        });
      } else {
        savedInstance = await createMutation.mutateAsync({
          feedbackId: selectedFeedbackId,
          versionNo: selectedVersionNo,
          assignments,
          frequency,
          startDate: fullStartDate,
          endDate: fullEndDate,
          status: 'scheduled',
          autoSendInvitations: sendEmailsNow && frequency !== 'once',
        });
      }

      const hasTemplateContent =
        invitationSubject.trim() !== '' ||
        invitationBody.trim() !== '' ||
        reminderSubject.trim() !== '' ||
        reminderBody.trim() !== '';

      if (hasTemplateContent) {
        await saveTemplateMutation.mutateAsync({
          instanceId: savedInstance.id,
          template: {
            invitationSubject,
            invitationBody,
            reminderSubject,
            reminderBody,
          },
        });
      }

      if (sendEmailsNow && !editingInstance) {
        const result = await invitationsMutation.mutateAsync({
          instanceId: savedInstance.id,
          subject: invitationSubject,
          body: invitationBody,
        });
        const count = result.totalRecipients;
        toast.success(`Sending invitations to ${count} recipient${count !== 1 ? 's' : ''}...`);
        setPollingInstances(prev => new Set(prev).add(savedInstance.id));
      }

      setShowCreateModal(false);
      resetForm();
    } catch (error: any) {
      if (error?.response?.status === 409 && sendEmailsNow && !editingInstance) {
        toast.error('An email send is already in progress for this instance');
      } else {
        console.error('Failed to save instance:', error);
        toast.error('Failed to create instance');
      }
    }
  };

  const resetForm = () => {
    setEditingInstance(null);
    setSelectedFeedbackId('');
    setSelectedVersionNo(1);
    setAssignments([]);
    setFrequency('once');
    setStartTime('09:00');
    setStartDate(new Date().toISOString().split('T')[0]);
    setEndDate('');
    setSendEmailsNow(true);
    setShowTemplateModal(false);
    setInvitationSubject('');
    setInvitationBody('');
    setReminderSubject('');
    setReminderBody('');
    setValidationErrors({});
  };

  const handleStatusToggle = async (instance: FeedbackInstance) => {
    const newStatus = instance.status === 'active' ? 'scheduled' : 'active';
    try {
      await updateMutation.mutateAsync({
        id: instance.id,
        data: { status: newStatus },
      });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      setDeleteTarget(null);
      toast.success('Instance deleted successfully');
    } catch (error) {
      setDeleteTarget(null);
      toast.error('Failed to delete instance');
      console.error('Failed to delete instance:', error);
    }
  };

  const handleEmailModalSend = async (subject: string, body: string) => {
    if (!emailModal) return;

    const { instanceId, type } = emailModal;
    setEmailModal(null);

    try {
      let result;
      if (type === 'invitation') {
        result = await invitationsMutation.mutateAsync({ instanceId, subject, body });
      } else {
        result = await remindersMutation.mutateAsync({ instanceId, subject, body });
      }
      const count = result.totalRecipients;
      toast.success(`Sending ${type === 'invitation' ? 'invitations' : 'reminders'} to ${count} recipient${count !== 1 ? 's' : ''}...`);
      setPollingInstances(prev => new Set(prev).add(instanceId));
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('An email send is already in progress for this instance');
      } else {
        toast.error(`Failed to send ${type === 'invitation' ? 'invitation' : 'reminder'} emails`);
      }
    }
  };

  const handlePollComplete = (instanceId: string) => {
    setPollingInstances(prev => {
      const next = new Set(prev);
      next.delete(instanceId);
      return next;
    });
  };

  const openEmailModal = (instance: FeedbackInstance, type: 'invitation' | 'reminder') => {
    const feedback = feedbackList.find(f => f.id === instance.feedbackId);
    setEmailModal({
      instanceId: instance.id,
      type,
      feedbackTitle: feedback?.title || feedback?.name || 'Feedback',
      deadline: instance.endDate,
      instance,
    });
  };

  const getAssignmentLabel = (instance: FeedbackInstance) => {
    if (instance.assignments && instance.assignments.length > 0) {
      const counts: Record<string, number> = {};
      for (const assignment of instance.assignments) {
        counts[assignment.scope] = (counts[assignment.scope] ?? 0) + 1;
      }
      const parts = (['account', 'project', 'employee'] as const)
        .filter(scope => counts[scope] > 0)
        .map(scope => `${counts[scope]} ${scope}${counts[scope] !== 1 ? 's' : ''}`);
      return parts.join(', ');
    }
    if (instance.assigneeType === 'group') {
      return `Group (${instance.assignedTo.length})`;
    }
    if (instance.assigneeType === 'user') {
      return `User (${instance.assignedTo.length})`;
    }
    return instance.assigneeType;
  };

  if (loadingFeedback || loadingInstances) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Feedback Instances</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Instance
        </button>
      </div>

      <div className="grid gap-4">
        {instances.map((instance) => {
          const feedback = feedbackList.find((s) => s.id === instance.feedbackId);
          if (!feedback) return null;

          return (
            <div
              key={instance.id}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {feedback.title || feedback.name}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        instance.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : instance.status === 'scheduled'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}
                    >
                      {instance.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>
                        {instance.frequency} - starts {new Date(instance.startDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <UsersIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>{getAssignmentLabel(instance)}</span>
                    </div>
                  </div>

                  <p className="mt-2 text-gray-600 dark:text-gray-400">{feedback.description}</p>
                  <EmailStatusBadge
                    instanceId={instance.id}
                    isPolling={pollingInstances.has(instance.id)}
                    onPollComplete={() => handlePollComplete(instance.id)}
                  />
                </div>

                <div className="flex items-center gap-2 sm:flex-shrink-0">
                  <button
                    onClick={() => navigate(`/analytics/${instance.id}`)}
                    className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900 rounded-md transition-colors"
                    title="View Results"
                    aria-label="View feedback results"
                  >
                    <BarChart2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </button>
                  {canManageInstances && (
                    <button
                      onClick={() => openEmailModal(instance, 'invitation')}
                      disabled={pollingInstances.has(instance.id)}
                      className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Send Invitations"
                      aria-label="Send invitation emails"
                    >
                      <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </button>
                  )}
                  {canManageInstances && (instance.status === 'active' || instance.status === 'completed') && (
                    <button
                      onClick={() => openEmailModal(instance, 'reminder')}
                      disabled={pollingInstances.has(instance.id)}
                      className="p-2 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Send Reminders"
                      aria-label="Send reminder emails to non-respondents"
                    >
                      <Send className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    </button>
                  )}
                  <button
                    onClick={() => handleStartEdit(instance)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-md transition-colors"
                    title="Edit"
                    aria-label="Edit feedback instance"
                  >
                    <PencilIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </button>
                  <button
                    onClick={() => handleStatusToggle(instance)}
                    className={`p-2 rounded-md transition-colors ${
                      instance.status === 'active'
                        ? 'hover:bg-orange-100 dark:hover:bg-orange-900'
                        : 'hover:bg-green-100 dark:hover:bg-green-900'
                    }`}
                    title={instance.status === 'active' ? 'Pause' : 'Activate'}
                    aria-label={instance.status === 'active' ? 'Pause instance' : 'Activate instance'}
                  >
                    {instance.status === 'active' ? (
                      <PauseIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    ) : (
                      <PlayIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                    )}
                  </button>
                  <button
                    onClick={() => setDeleteTarget({ id: instance.id, name: feedback.title || feedback.name || 'this instance' })}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded-md transition-colors"
                    title="Delete"
                    aria-label="Delete feedback instance"
                  >
                    <TrashIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {instances.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-600 mb-4">
              <CalendarIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No instances yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first feedback instance to start collecting responses
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Instance
            </button>
          </div>
        )}
      </div>

      {emailModal && (
        <EmailComposeModal
          type={emailModal.type}
          feedbackTitle={emailModal.feedbackTitle}
          deadline={emailModal.deadline}
          savedSubject={emailModal.type === 'invitation' ? emailModal.instance.customInvitationSubject : emailModal.instance.customReminderSubject}
          savedBody={emailModal.type === 'invitation' ? emailModal.instance.customInvitationBody : emailModal.instance.customReminderBody}
          onSend={handleEmailModalSend}
          onClose={() => setEmailModal(null)}
        />
      )}

      {showTemplateModal && (
        <EmailTemplateModal
          invitationSubject={invitationSubject}
          invitationBody={invitationBody}
          reminderSubject={reminderSubject}
          reminderBody={reminderBody}
          onSave={(template) => {
            setInvitationSubject(template.invitationSubject);
            setInvitationBody(template.invitationBody);
            setReminderSubject(template.reminderSubject);
            setReminderBody(template.reminderBody);
            setShowTemplateModal(false);
          }}
          onClose={() => setShowTemplateModal(false)}
        />
      )}

      {showAssignToModal && (
        <AssignToModal
          assignments={assignments}
          onSave={(newAssignments) => {
            setAssignments(newAssignments);
            setShowAssignToModal(false);
            setValidationErrors(prev => ({ ...prev, assignments: undefined }));
          }}
          onClose={() => setShowAssignToModal(false)}
        />
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingInstance ? 'Edit Feedback Instance' : 'Create Feedback Instance'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Feedback <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedFeedbackId}
                    onChange={(e) => {
                      setSelectedFeedbackId(e.target.value);
                      setValidationErrors((prev) => ({ ...prev, feedback: undefined }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                      validationErrors.feedback
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    <option value="">Select a feedback</option>
                    {feedbackList.map((feedbackItem) => (
                      <option key={feedbackItem.id} value={feedbackItem.id}>
                        {feedbackItem.title || feedbackItem.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.feedback && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.feedback}</p>
                  )}
                </div>

                {selectedFeedbackId && publishedVersions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Version <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedVersionNo}
                      onChange={(e) => setSelectedVersionNo(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {publishedVersions.map((version) => (
                        <option key={version.versionNo} value={version.versionNo}>
                          Version {version.versionNo}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Assign To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Assign To <span className="text-red-500">*</span>
                  </label>

                  {editingInstance && isLegacyInstance(editingInstance) ? (
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Legacy assignment:</span>{' '}
                      {editingInstance.assigneeType === 'group'
                        ? `Group (${editingInstance.assignedTo.length})`
                        : `User (${editingInstance.assignedTo.length})`}
                      <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                        Legacy assignments are read-only. Create a new instance to use the new assignment system.
                      </p>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowAssignToModal(true)}
                        className={`w-full px-3 py-2 border rounded-md text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.assignments
                            ? 'border-red-500 dark:border-red-500'
                            : 'border-gray-300 dark:border-gray-600'
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors`}
                      >
                        <span className={assignments.length === 0 ? 'text-gray-400' : ''}>
                          {assignments.length === 0
                            ? 'Select assignments...'
                            : `${assignments.length} assignment${assignments.length !== 1 ? 's' : ''} selected`}
                        </span>
                        <UsersIcon className="h-4 w-4 text-gray-400" />
                      </button>
                      {validationErrors.assignments && (
                        <p className="mt-1 text-sm text-red-500">{validationErrors.assignments}</p>
                      )}
                      {assignments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {assignments.slice(0, 5).map((a, idx) => {
                            const ChipIcon = a.scope === 'account' ? Building2 : a.scope === 'project' ? FolderOpen : UsersIcon;
                            return (
                              <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                <ChipIcon className="h-3 w-3" />
                                {a.name}
                              </span>
                            );
                          })}
                          {assignments.length > 5 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              +{assignments.length - 5} more
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Frequency
                  </label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="once">Once</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {frequency !== 'once' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Time of Day
                    </label>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Each occurrence will start at this time (UTC)
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Email Notifications</h3>
                  <div className="flex flex-col gap-3">
                    {(!editingInstance || (editingInstance && editingInstance.frequency !== 'once')) && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={sendEmailsNow}
                          onChange={(e) => setSendEmailsNow(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                        />
                        {!editingInstance
                          ? (frequency === 'once'
                            ? 'Send invitation emails after creation'
                            : 'Automatically send invitation emails for each occurrence')
                          : 'Automatically send invitation emails for each occurrence'}
                      </label>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowTemplateModal(true)}
                      className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      <Mail className="h-4 w-4" />
                      Customise Email Template
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveInstance}
                  disabled={!selectedFeedbackId || (!(editingInstance && isLegacyInstance(editingInstance)) && assignments.length === 0)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {editingInstance ? 'Save' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmDeleteModal
          instanceName={deleteTarget.name}
          onConfirm={() => handleDelete(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default InstanceManager;
